import { theme } from './theme';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Badge,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  ThemeProvider,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import HomeIcon from '@mui/icons-material/Home';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonIcon from '@mui/icons-material/Person';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import PaletteIcon from '@mui/icons-material/Palette';
import ForumIcon from '@mui/icons-material/Forum';
import SyncIcon from '@mui/icons-material/Sync';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TodayIcon from '@mui/icons-material/Today';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import MenuIcon from '@mui/icons-material/Menu';
import { AvailabilityMatrix } from './components/AvailabilityMatrix';
import { AccountManagement } from './components/AccountManagement';
import { AppSettings } from './components/AppSettings';
import { AuthenticatedPlayerView } from './components/AuthenticatedPlayerView';
import { AskCourtCaptain, askCourtCaptainVisible } from './components/AskCourtCaptain';
import { CrudPage } from './components/CrudPage';
import { CommandCenter } from './components/CommandCenter';
import { Communications } from './components/Communications';
import { EventEditor } from './components/EventEditor';
import { FeesPayments } from './components/FeesPayments';
import { LineupBuilder } from './components/LineupBuilder';
import { Login } from './components/Login';
import { MatchScheduling } from './components/MatchScheduling';
import { MatchFormats } from './components/MatchFormats';
import { ParticipationEventsManager } from './components/ParticipationEventsManager';
import { PlanningBoard } from './components/PlanningBoard';
import { Reports } from './components/Reports';
import { RecommendationStrategies } from './components/RecommendationStrategies';
import { RosterManager } from './components/RosterManager';
import { SeasonPlanning } from './components/SeasonPlanning';
import { TeamManager } from './components/TeamManager';
import { TeamEvents } from './components/TeamEvents';
import { TennisRecordSyncSettings } from './components/TennisRecordSyncSettings';
import { WorkspaceBranding } from './components/WorkspaceBranding';
import { GroupMeSpike } from './components/GroupMeSpike';
import { NotificationSettings } from './components/NotificationSettings';
import { OAuthConsent } from './components/OAuthConsent';
import { PasswordSetup } from './components/PasswordSetup';
import { UserManagement } from './components/UserManagement';
import { useAuth } from './auth/AuthContext';
import { entities, getEntity } from './data/entities';
import { loadLookups } from './data/lookups';
import type { AppSettings as AppSettingsType, LookupState, TeamPermissionFeature, UserPreferences } from './types';
import { loadUserPreferences, saveUserPreferences } from './utils/playerPreferences';
import { defaultAppSettings, loadAppSettings, saveAppSettings } from './utils/timeFormat';
import { supabase } from './config/supabase';
import { pairingCompatibilityAnalyticsEnabled } from './utils/featureFlags';
import type { BreadcrumbItem } from './utils/uxNavigation';
import { defaultCaptainTeamIds as captainedTeamIdsForAssignments } from './utils/commandCenterResponsibility';

const drawerWidth = 280;

const navSections = [
  {
    label: 'CAPTAIN WORKFLOW',
    items: [
      { key: 'command-center', label: 'Command Center', icon: <HomeIcon />, path: '/' },
      { key: 'planning-board', label: 'Planning Board', icon: <ViewModuleIcon />, path: '/planning-board', feature: 'planning_board' },
      { key: 'season-planning', label: 'Season Planning', icon: <AutoFixHighIcon />, feature: 'season_planning' },
      { key: 'team-events', label: 'Team Events', icon: <TodayIcon />, path: '/team-events', feature: 'team_events' },
      { key: 'fees-payments', label: 'Fees & Payments', icon: <PaymentsIcon />, feature: 'fees_payments' },
      { key: 'communications', label: 'Communications', icon: <MailOutlineIcon />, path: '/communications', feature: 'communications' },
      { key: 'reports', label: 'Reports', icon: <AssessmentIcon />, feature: 'reports' },
    ],
  },
  {
    label: 'SETUP & ADMINISTRATION',
    items: [
      { key: 'teams', label: 'Teams', icon: <SportsTennisIcon />, feature: 'roster_management' },
      { key: 'players', label: 'Players', icon: <PersonIcon />, feature: 'roster_management' },
      { key: 'facilities', label: 'Facilities', icon: <HomeWorkIcon /> },
      { key: 'organizations', label: 'Organizations', icon: <BusinessIcon /> },
      { key: 'match-formats', label: 'Match Formats', icon: <ListAltIcon /> },
      { key: 'recommendation-strategies', label: 'Cross-Team Scheduling Policies', icon: <SettingsIcon /> },
      { key: 'notification-settings', label: 'Notification Settings', icon: <SettingsIcon /> },
    ],
  },
  {
    label: 'SYSTEM ADMINISTRATION & AUDIT',
    items: [
      { key: 'player', label: 'My Player View', icon: <SportsTennisIcon />, path: '/player', playerViewOnly: true },
      { key: 'account', label: 'Account', icon: <AccountCircleIcon />, path: '/account' },
      { key: 'participation-events', label: 'Participation Log', icon: <HistoryIcon />, feature: 'matches_lineups' },
      { key: 'users', label: 'Users', icon: <ManageAccountsIcon />, feature: 'users_permissions' },
      {
        key: 'workspace-branding',
        label: 'Workspace Branding',
        icon: <PaletteIcon />,
        path: '/settings/workspace-branding',
        adminOnly: true,
        playerPortalOnly: true,
      },
      { key: 'app-settings', label: 'App Settings', icon: <SettingsIcon />, path: '/settings/integrations/gmail' },
    ],
  },
  {
    label: 'INTEGRATIONS',
    items: [
      { key: 'groupme-spike', label: 'GroupMe RSVP Sync', icon: <ForumIcon />, path: '/settings/integrations/groupme', adminOnly: true },
      { key: 'tennisrecord-sync', label: 'TennisRecord Sync', icon: <SyncIcon />, path: '/settings/integrations/tennisrecord', adminOnly: true },
    ],
  },
];

const pageTitles = new Map(
  navSections.flatMap((section) => section.items.map((item) => [item.key, item.label] as const)),
);

const emptyLookups: LookupState = {
  app_users: [],
  workspaces: [],
  organizations: [],
  league_configurations: [],
  facilities: [],
  teams: [],
  players: [],
  league_configuration_positions: [],
  recommendation_strategy_profiles: [],
};

type LineupReturnTarget = {
  page: string;
  matchId?: string | null;
  teamId?: string | null;
};

type TeamReturnTarget = {
  page: string;
  matchId?: string | null;
  teamId?: string | null;
  reportType?: 'player_schedule' | 'player_participation' | 'player_results' | 'partner_results' | 'partner_compatibility' | 'team_schedule' | 'team_results' | null;
  feesView?: 'campaigns' | 'outstanding' | 'credits' | 'payment-inbox' | 'payment-import-settings';
};

type MatchReturnTarget = {
  page: 'matches' | 'team-events' | 'command-center' | 'planning-board';
};

type EventEditorReturnTarget =
  | { page: 'team-events' }
  | { page: 'match-detail'; matchId: string; matchReturnTarget: MatchReturnTarget | null }
  | { page: 'team-event-detail'; eventId: string };

type EventEditorLaunchOrigin = 'team-events' | 'match-detail' | 'team-event-detail';

type AvailabilityReturnTarget = {
  page: 'planning-board' | 'match-detail' | 'command-center' | 'team-events' | 'lineup';
  matchId?: string | null;
  matchReturnTarget?: MatchReturnTarget | null;
};

type AvailabilityOpenContext = {
  teamId?: string | null;
  matchId?: string | null;
  returnTarget?: AvailabilityReturnTarget['page'];
};

type CourtCaptainViewMode = 'captain' | 'player';

function planningWorkflowFullScreenStorageKey(userId: string | null | undefined): string {
  return `${userId || 'anonymous'}:planning-board:workflow-full-screen`;
}

function readPlanningWorkflowFullScreen(userId: string | null | undefined): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(planningWorkflowFullScreenStorageKey(userId)) === 'true';
  } catch {
    return false;
  }
}

function savePlanningWorkflowFullScreen(userId: string | null | undefined, next: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(planningWorkflowFullScreenStorageKey(userId), next ? 'true' : 'false');
  } catch {
    // Ignore storage failures; the visible workflow state still updates.
  }
}

function preferredViewStorageKey(userId: string | null | undefined): string {
  return `${userId || 'anonymous'}:courtcaptain:preferred-view`;
}

function readPreferredView(userId: string | null | undefined): CourtCaptainViewMode {
  if (typeof window === 'undefined') return 'captain';
  try {
    return window.localStorage.getItem(preferredViewStorageKey(userId)) === 'player' ? 'player' : 'captain';
  } catch {
    return 'captain';
  }
}

function savePreferredView(userId: string | null | undefined, viewMode: CourtCaptainViewMode) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(preferredViewStorageKey(userId), viewMode);
  } catch {
    // Ignore storage failures; explicit navigation still works.
  }
}

function initialRoute(): { page: string; matchId: string | null; teamEventId: string | null; eventEditorId: string | null; eventEditorMode: 'create' | 'edit' | null; origin: 'planning-board' | null; availabilityTeamId: string | null } {
  if (window.location.pathname === '/seasons' || window.location.pathname.startsWith('/seasons/')) {
    window.history.replaceState({}, '', '/teams');
    return { page: 'teams', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  }
  const query = new URLSearchParams(window.location.search);
  const origin = query.get('origin') === 'planning-board' ? 'planning-board' : null;
  const availabilityTeamId = query.get('teamId');
  const availabilityMatchId = query.get('matchId');
  const lineupMatch = window.location.pathname.match(/^\/matches\/([^/]+)\/lineup\/?$/);
  if (lineupMatch?.[1]) return { page: 'lineup', matchId: lineupMatch[1], teamEventId: null, eventEditorId: null, eventEditorMode: null, origin, availabilityTeamId: null };
  if (window.location.pathname === '/team-events/new' || window.location.pathname === '/matches/new') return { page: 'event-editor', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: 'create', origin, availabilityTeamId: null };
  const teamEventEditMatch = window.location.pathname.match(/^\/team-events\/([^/]+)\/edit\/?$/);
  if (teamEventEditMatch?.[1]) return { page: 'event-editor', matchId: null, teamEventId: null, eventEditorId: teamEventEditMatch[1], eventEditorMode: 'edit', origin, availabilityTeamId: null };
  const detailMatch = window.location.pathname.match(/^\/matches\/([^/]+)\/?$/);
  if (detailMatch?.[1]) return { page: 'match-detail', matchId: detailMatch[1], teamEventId: null, eventEditorId: null, eventEditorMode: null, origin, availabilityTeamId: null };
  const teamEventMatch = window.location.pathname.match(/^\/team-events\/([^/]+)\/?$/);
  if (teamEventMatch?.[1]) return { page: 'team-events', matchId: null, teamEventId: teamEventMatch[1], eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (window.location.pathname === '/matches') {
    return { page: 'team-events', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  }
  if (window.location.pathname === '/availability') return { page: 'availability', matchId: availabilityMatchId, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin, availabilityTeamId };
  if (window.location.pathname === '/planning-board') return { page: 'planning-board', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (window.location.pathname === '/team-events') return { page: 'team-events', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (window.location.pathname === '/groupme-events') return { page: 'team-events', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (window.location.pathname === '/communications') return { page: 'communications', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (window.location.pathname === '/settings/workspace-branding') return { page: 'workspace-branding', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (window.location.pathname === '/settings/integrations/gmail') return { page: 'app-settings', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (window.location.pathname === '/settings/integrations/groupme') return { page: 'groupme-spike', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (window.location.pathname === '/settings/integrations/tennisrecord') return { page: 'tennisrecord-sync', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (window.location.pathname === '/settings/pairing-compatibility/backfill') {
    window.history.replaceState({}, '', '/settings/integrations/gmail#partner-compatibility');
    return { page: 'app-settings', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  }
  if (window.location.pathname === '/oauth/consent') return { page: 'oauth-consent', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (window.location.pathname === '/account') return { page: 'account', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  if (['/player', '/player/fees', '/player/preferences'].includes(window.location.pathname)) return { page: 'player', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
  return { page: 'command-center', matchId: null, teamEventId: null, eventEditorId: null, eventEditorMode: null, origin: null, availabilityTeamId: null };
}

export function App() {
  const {
    loading: authLoading,
    session,
    appUser,
    appUserId,
    signOut,
    isOwner,
    teamAssignments,
    readableTeamIds,
    writableTeamIds,
    passwordSetupRequired,
    canViewAnyTeamFeature,
    canEditAnyTeamFeature,
    readableTeamIdsForFeature,
  } = useAuth();
  const initialMatchesPath = window.location.pathname === '/matches';
  const initial = initialRoute();
  if (initialMatchesPath) window.history.replaceState({}, '', '/team-events');
  const isMobileNav = useMediaQuery(theme.breakpoints.down('md'));
  const [page, setPage] = useState(initial.page);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [lookups, setLookups] = useState<LookupState>(emptyLookups);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [requestedMatchId, setRequestedMatchId] = useState<string | null>(initial.page === 'match-detail' ? initial.matchId : null);
  const [requestedScoreMatchId, setRequestedScoreMatchId] = useState<string | null>(null);
  const [requestedEditMatchId, setRequestedEditMatchId] = useState<string | null>(null);
  const [requestedCreateMatch, setRequestedCreateMatch] = useState(window.location.pathname === '/matches/new');
  const [requestedTeamMatchFilterId, setRequestedTeamMatchFilterId] = useState<string | null>(null);
  const [requestedLineupMatchId, setRequestedLineupMatchId] = useState<string | null>(initial.page === 'lineup' ? initial.matchId : null);
  const [requestedCommunicationsMatchId, setRequestedCommunicationsMatchId] = useState<string | null>(null);
  useEffect(() => {
    if (page !== 'communications') setRequestedCommunicationsMatchId(null);
  }, [page]);
  const [lineupReturnTarget, setLineupReturnTarget] = useState<LineupReturnTarget | null>(
    initial.page === 'lineup' && initial.origin === 'planning-board' ? { page: 'planning-board', matchId: initial.matchId } : null,
  );
  const [teamReturnTarget, setTeamReturnTarget] = useState<TeamReturnTarget | null>(null);
  const [matchReturnTarget, setMatchReturnTarget] = useState<MatchReturnTarget | null>(
    initial.page === 'match-detail' && initial.origin === 'planning-board' ? { page: 'planning-board' } : null,
  );
  const [requestedParticipationPlayerId, setRequestedParticipationPlayerId] = useState<string | null>(null);
  const [requestedSchedulePlayerId, setRequestedSchedulePlayerId] = useState<string | null>(null);
  const [requestedParticipationTeamId, setRequestedParticipationTeamId] = useState<string | null>(null);
  const [requestedAvailabilityTeamId, setRequestedAvailabilityTeamId] = useState<string | null>(initial.page === 'availability' ? initial.availabilityTeamId : null);
  const [requestedAvailabilityMatchId, setRequestedAvailabilityMatchId] = useState<string | null>(initial.page === 'availability' ? initial.matchId : null);
  const [availabilityReturnTarget, setAvailabilityReturnTarget] = useState<AvailabilityReturnTarget | null>(
    initial.page === 'availability' && initial.origin === 'planning-board' ? { page: 'planning-board', matchId: initial.matchId } : null,
  );
  const [requestedRosterTeamId, setRequestedRosterTeamId] = useState<string | null>(null);
  const [requestedTeamDetailId, setRequestedTeamDetailId] = useState<string | null>(null);
  const [requestedTeamEventId, setRequestedTeamEventId] = useState<string | null>(initial.teamEventId);
  const [requestedEventEditorId, setRequestedEventEditorId] = useState<string | null>(initial.eventEditorId);
  const [requestedEventEditorMode, setRequestedEventEditorMode] = useState<'create' | 'edit'>(initial.eventEditorMode || 'create');
  const [teamEventsRefreshToken, setTeamEventsRefreshToken] = useState(0);
  const [eventEditorReturnTarget, setEventEditorReturnTarget] = useState<EventEditorReturnTarget | null>(
    initial.page === 'event-editor' && initial.eventEditorMode === 'edit' && initial.eventEditorId
      ? { page: 'match-detail', matchId: initial.eventEditorId, matchReturnTarget: initial.origin === 'planning-board' ? { page: 'planning-board' } : { page: 'team-events' } }
      : { page: 'team-events' },
  );
  const [requestedTeamEventsFilter, setRequestedTeamEventsFilter] = useState<{ teamId?: string | null; eventTypeFilter: 'Match' } | null>(
    initialMatchesPath ? { eventTypeFilter: 'Match' } : null,
  );
  const [requestedReportType, setRequestedReportType] = useState<'player_schedule' | 'player_participation' | 'player_results' | 'partner_results' | 'partner_compatibility' | 'team_schedule' | 'team_results' | null>(null);
  const [requestedFeesView, setRequestedFeesView] = useState<'campaigns' | 'outstanding' | 'credits' | 'payment-inbox' | 'payment-import-settings'>('campaigns');
  const [userPreferences, setUserPreferences] = useState<UserPreferences>(() => loadUserPreferences());
  const [appSettings, setAppSettings] = useState<AppSettingsType>(defaultAppSettings);
  const [playerPortalEnabled, setPlayerPortalEnabled] = useState(false);
  const [playerPortalStatusLoaded, setPlayerPortalStatusLoaded] = useState(false);
  const [availabilityBadgeCount, setAvailabilityBadgeCount] = useState(0);
  const [showAuthReset, setShowAuthReset] = useState(false);
  const [askCourtCaptainOpen, setAskCourtCaptainOpen] = useState(false);
  const [planningBoardFullScreen, setPlanningBoardFullScreenState] = useState(() => readPlanningWorkflowFullScreen(appUserId));
  const setPlanningBoardFullScreen = useCallback((next: boolean) => {
    setPlanningBoardFullScreenState(next);
    savePlanningWorkflowFullScreen(appUserId, next);
  }, [appUserId]);

  const visibleLookups = useMemo<LookupState>(() => {
    if (isOwner) return lookups;
    const readable = new Set(readableTeamIds);
    return {
      ...lookups,
      teams: lookups.teams.filter((team) => readable.has(team.value)),
    };
  }, [isOwner, lookups, readableTeamIds]);
  const visibleLookupsForFeature = useCallback((feature: TeamPermissionFeature): LookupState => {
    if (isOwner) return lookups;
    const readable = new Set(readableTeamIdsForFeature(feature));
    return {
      ...lookups,
      teams: lookups.teams.filter((team) => readable.has(team.value)),
    };
  }, [isOwner, lookups, readableTeamIdsForFeature]);
  // Responsibility is separate from authorization: owners retain every team in
  // their selectors, but only explicit Captain/Co-Captain teams are preselected.
  const defaultCaptainTeamIds = useMemo(() => captainedTeamIdsForAssignments(
    teamAssignments,
    lookups.teams.length > 0
      ? lookups.teams.filter((team) => team.is_active !== false).map((team) => team.value)
      : teamAssignments.map((assignment) => assignment.team_id),
  ), [lookups.teams, teamAssignments]);
  const deprecatedMatchesFilter = useMemo(
    () => (page === 'matches' ? { teamId: requestedTeamMatchFilterId, eventTypeFilter: 'Match' as const } : null),
    [page, requestedTeamMatchFilterId],
  );
  const commandCenterFeatureAccess = useMemo<Record<TeamPermissionFeature, boolean>>(() => ({
    planning_board: canViewAnyTeamFeature('planning_board'),
    season_planning: canViewAnyTeamFeature('season_planning'),
    matches_lineups: canViewAnyTeamFeature('matches_lineups'),
    team_events: canViewAnyTeamFeature('team_events'),
    player_availability: canViewAnyTeamFeature('player_availability'),
    fees_payments: canViewAnyTeamFeature('fees_payments'),
    communications: canViewAnyTeamFeature('communications'),
    reports: canViewAnyTeamFeature('reports'),
    roster_management: canViewAnyTeamFeature('roster_management'),
    partner_preferences: canViewAnyTeamFeature('partner_preferences'),
    users_permissions: canViewAnyTeamFeature('users_permissions'),
  }), [canViewAnyTeamFeature]);
  const hasTeamAccess = isOwner || readableTeamIds.length > 0;
  const viewerOnly = !isOwner && readableTeamIds.length > 0 && writableTeamIds.length === 0;
  const playerViewAvailable = playerPortalEnabled && Boolean(appUser?.linked_player_id);
  const canUseAskCourtCaptain = askCourtCaptainVisible(hasTeamAccess);
  const planningWorkflowFullScreenActive = !isMobileNav && planningBoardFullScreen && (
    page === 'planning-board'
    || (page === 'match-detail' && matchReturnTarget?.page === 'planning-board')
    || (page === 'availability' && availabilityReturnTarget?.page === 'planning-board')
    || (page === 'lineup' && lineupReturnTarget?.page === 'planning-board')
  );
  const eventEditorBackLabel: string | undefined = eventEditorReturnTarget?.page === 'match-detail'
    ? 'Match'
    : eventEditorReturnTarget?.page === 'team-event-detail'
      ? undefined
      : 'Team Events';
  const askCourtCaptainTeamId = (
    page === 'availability' ? requestedAvailabilityTeamId
      : page === 'rosters' ? requestedRosterTeamId
        : page === 'teams' ? requestedTeamDetailId
          : null
  ) || visibleLookups.teams[0]?.value || null;
  const askCourtCaptainMatchId = page === 'lineup'
    ? requestedLineupMatchId
    : page === 'match-detail'
      ? requestedMatchId
      : null;

  const refreshLookups = async () => {
    if (!appUserId) return;
    try {
      setLookups(await loadLookups(userPreferences));
      setLookupError(null);
    } catch (error) {
      setLookupError(error instanceof Error ? error.message : 'Unable to load lookup data.');
    }
  };

  useEffect(() => {
    if (appUserId) void refreshLookups();
  }, [appUserId, userPreferences]);

  useEffect(() => {
    if (!authLoading) {
      setShowAuthReset(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowAuthReset(true), 8000);
    return () => window.clearTimeout(timer);
  }, [authLoading]);

  const refreshAvailabilityBadge = useCallback(async () => {
    if (!appUserId) {
      setAvailabilityBadgeCount(0);
      return;
    }
    const { data, error } = await supabase.rpc('get_groupme_availability_unread_count');
    if (error) {
      setAvailabilityBadgeCount(0);
      return;
    }
    setAvailabilityBadgeCount(Math.max(0, Number(data) || 0));
  }, [appUserId]);

  useEffect(() => {
    void refreshAvailabilityBadge();
    const refreshOnFocus = () => void refreshAvailabilityBadge();
    const interval = window.setInterval(refreshOnFocus, 60_000);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [refreshAvailabilityBadge]);

  useEffect(() => {
    void loadAppSettings().then(setAppSettings);
  }, []);

  useEffect(() => {
    if (!session) {
      setPlayerPortalEnabled(false);
      setPlayerPortalStatusLoaded(true);
      return;
    }
    setPlayerPortalStatusLoaded(false);
    void fetch('/api/player-portal/status')
      .then((response) => response.json())
      .then((status: { enabled?: boolean }) => setPlayerPortalEnabled(status.enabled === true))
      .catch(() => setPlayerPortalEnabled(false))
      .finally(() => setPlayerPortalStatusLoaded(true));
  }, [session]);

  useEffect(() => {
    if (!playerPortalStatusLoaded || playerPortalEnabled || page !== 'workspace-branding') return;
    window.history.replaceState({}, '', '/');
    setPage('command-center');
  }, [page, playerPortalEnabled, playerPortalStatusLoaded]);

  useEffect(() => {
    if (!appUser || !playerPortalStatusLoaded) return;
    if (appUser.role === 'player') {
      savePreferredView(appUser.id, 'player');
      if (page === 'player') return;
      window.history.replaceState({}, '', '/player');
      setPage('player');
      return;
    }
    if (!playerViewAvailable) return;
    if (page !== 'command-center' || window.location.pathname !== '/') return;
    if (readPreferredView(appUser.id) !== 'player') return;
    window.history.replaceState({}, '', '/player');
    setPage('player');
  }, [appUser, page, playerPortalStatusLoaded, playerViewAvailable]);

  useEffect(() => {
    const pageTitle = page === 'match-detail'
      ? 'Match Detail'
      : page === 'lineup'
        ? 'Lineup Builder'
        : pageTitles.get(page) || 'Command Center';
    document.title = `CourtCaptain - ${pageTitle}`;
  }, [page]);

  const updateUserPreferences = (patch: Partial<UserPreferences>) => {
    setUserPreferences((current) => {
      const next = { ...current, ...patch };
      saveUserPreferences(next);
      return next;
    });
  };

  const updateAppSettings = async (settings: AppSettingsType) => {
    await saveAppSettings(settings);
    setAppSettings(settings);
  };

  const pageFeature = (key: string): TeamPermissionFeature | null => {
    if (['matches', 'match-detail', 'lineup', 'participation-events'].includes(key)) return 'matches_lineups';
    if (key === 'availability') return 'player_availability';
    if (key === 'planning-board') return 'planning_board';
    if (key === 'season-planning') return 'season_planning';
    if (key === 'fees-payments') return 'fees_payments';
    if (key === 'communications') return 'communications';
    if (key === 'team-events' || key === 'groupme-events' || key === 'event-editor') return 'team_events';
    if (key === 'reports') return 'reports';
    if (key === 'teams' || key === 'rosters' || key === 'players') return 'roster_management';
    if (key === 'users') return 'users_permissions';
    return null;
  };

  const ownerOnlyPages = new Set(['app-settings', 'groupme-spike', 'tennisrecord-sync', 'workspace-branding']);
  const canAccessPage = (key: string): boolean => {
    if (key === 'player') return playerPortalEnabled && Boolean(appUser?.linked_player_id);
    if (key === 'command-center' || key === 'oauth-consent' || key === 'account') return true;
    if (ownerOnlyPages.has(key)) return isOwner;
    const feature = pageFeature(key);
    return feature ? canViewAnyTeamFeature(feature) : isOwner;
  };

  const currentEntity = useMemo(() => {
    if (['command-center', 'player', 'account', 'rosters', 'teams', 'matches', 'match-detail', 'availability', 'lineup', 'participation-events', 'planning-board', 'season-planning', 'reports', 'fees-payments', 'communications', 'team-events', 'event-editor', 'groupme-events', 'match-formats', 'recommendation-strategies', 'notification-settings', 'workspace-branding', 'app-settings', 'groupme-spike', 'tennisrecord-sync', 'oauth-consent', 'users'].includes(page)) return null;
    return getEntity(page);
  }, [page]);

  const planningOriginQuery = (returnTarget?: { page?: string } | null) => (returnTarget?.page === 'planning-board' ? '?origin=planning-board' : '');

  const openMatch = (matchId: string, returnTarget?: MatchReturnTarget) => {
    setRequestedScoreMatchId(null);
    setRequestedCreateMatch(false);
    setRequestedMatchId(matchId);
    setMatchReturnTarget(returnTarget || null);
    window.history.pushState({}, '', `/matches/${matchId}${planningOriginQuery(returnTarget)}`);
    setPage('match-detail');
  };

  const openMatchScores = (matchId: string) => {
    setRequestedCreateMatch(false);
    setRequestedMatchId(matchId);
    setRequestedScoreMatchId(matchId);
    setMatchReturnTarget(page === 'team-events' ? { page: 'team-events' } : page === 'command-center' ? { page: 'command-center' } : null);
    window.history.pushState({}, '', `/matches/${matchId}`);
    setPage('match-detail');
  };

  const openMatchListInTeamEvents = (teamId?: string | null) => {
    setRequestedTeamEventsFilter({ teamId: teamId || null, eventTypeFilter: 'Match' });
    setRequestedMatchId(null);
    setRequestedEditMatchId(null);
    setRequestedCreateMatch(false);
    setRequestedTeamEventId(null);
    setRequestedEventEditorId(null);
    window.history.pushState({}, '', '/team-events');
    setPage('team-events');
  };

  const returnToTeamEvents = () => {
    setRequestedMatchId(null);
    setRequestedEditMatchId(null);
    setRequestedCreateMatch(false);
    setRequestedTeamEventId(null);
    setRequestedEventEditorId(null);
    window.history.pushState({}, '', '/team-events');
    setPage('team-events');
  };

  const openCreateEvent = () => {
    setRequestedEventEditorMode('create');
    setRequestedEventEditorId(null);
    setEventEditorReturnTarget({ page: 'team-events' });
    window.history.pushState({}, '', '/team-events/new');
    setPage('event-editor');
  };

  const openEditTeamEvent = (eventId: string, origin: EventEditorLaunchOrigin = 'team-events') => {
    setRequestedEventEditorMode('edit');
    setRequestedEventEditorId(eventId);
    setEventEditorReturnTarget(origin === 'team-event-detail' ? { page: 'team-event-detail', eventId } : { page: 'team-events' });
    window.history.pushState({}, '', `/team-events/${eventId}/edit`);
    setPage('event-editor');
  };

  const openEditMatchEvent = (matchId: string, origin: EventEditorLaunchOrigin = 'match-detail', returnTarget: MatchReturnTarget | null = matchReturnTarget) => {
    setRequestedEventEditorMode('edit');
    setRequestedEventEditorId(matchId);
    setEventEditorReturnTarget(origin === 'team-events' ? { page: 'team-events' } : { page: 'match-detail', matchId, matchReturnTarget: returnTarget || { page: 'team-events' } });
    window.history.pushState({}, '', `/team-events/${matchId}/edit${planningOriginQuery(returnTarget)}`);
    setPage('event-editor');
  };

  const returnFromEventEditor = (result?: { kind?: 'match' | 'team-event'; id?: string }) => {
    const target = eventEditorReturnTarget;
    setRequestedEventEditorId(null);
    setRequestedEventEditorMode('create');
    setEventEditorReturnTarget(null);
    if (result?.id) {
      setTeamEventsRefreshToken((current) => current + 1);
    }

    if (result?.kind === 'match' && result.id) {
      if (target?.page === 'team-events') {
        returnToTeamEvents();
        return;
      }
      openMatch(result.id, target?.page === 'match-detail' ? target.matchReturnTarget || undefined : { page: 'team-events' });
      return;
    }
    if (result?.kind === 'team-event' && result.id) {
      if (target?.page === 'team-events') {
        returnToTeamEvents();
        return;
      }
      setRequestedTeamEventId(result.id);
      window.history.pushState({}, '', `/team-events/${result.id}`);
      setPage('team-events');
      return;
    }
    if (target?.page === 'match-detail') {
      openMatch(target.matchId, target.matchReturnTarget || undefined);
      return;
    }
    if (target?.page === 'team-event-detail') {
      setRequestedTeamEventId(target.eventId);
      window.history.pushState({}, '', `/team-events/${target.eventId}`);
      setPage('team-events');
      return;
    }
    returnToTeamEvents();
  };

  const returnFromDeletedEventEditor = () => {
    const target = eventEditorReturnTarget;
    setRequestedEventEditorId(null);
    setRequestedEventEditorMode('create');
    setEventEditorReturnTarget(null);
    setTeamEventsRefreshToken((current) => current + 1);

    if (target?.page === 'match-detail' && target.matchReturnTarget?.page === 'planning-board') {
      window.history.pushState({}, '', '/planning-board');
      setPage('planning-board');
      return;
    }
    returnToTeamEvents();
  };

  const openLineup = (matchId: string, context?: { teamId?: string | null; returnTarget?: LineupReturnTarget['page'] }) => {
    const returnPage = context?.returnTarget || page;
    setLineupReturnTarget({
      page: returnPage,
      matchId: page === 'match-detail' ? requestedMatchId || matchId : null,
      teamId: context?.teamId ?? (page === 'availability' ? requestedAvailabilityTeamId : null),
    });
    setRequestedLineupMatchId(matchId);
    window.history.pushState({}, '', `/matches/${matchId}/lineup${returnPage === 'planning-board' ? '?origin=planning-board' : ''}`);
    setPage('lineup');
  };

  const backFromLineup = (matchId: string) => {
    const target = lineupReturnTarget;
    setLineupReturnTarget(null);
    setRequestedLineupMatchId(null);

    if (!target) {
      openMatch(matchId);
      return;
    }

    if (target.page === 'availability') {
      setRequestedAvailabilityTeamId(target.teamId || null);
      setRequestedAvailabilityMatchId(matchId);
      const params = new URLSearchParams({ matchId });
      if (target.teamId) params.set('teamId', target.teamId);
      window.history.pushState({}, '', `/availability?${params.toString()}`);
      setPage('availability');
      return;
    }

    if (target.page === 'matches') {
      openMatchListInTeamEvents(target.teamId);
      return;
    }

    if (target.page === 'match-detail') {
      openMatch(target.matchId || matchId, matchReturnTarget || undefined);
      return;
    }

    if (target.page === 'command-center') {
      window.history.pushState({}, '', '/');
      setPage('command-center');
      return;
    }

    if (target.page === 'planning-board') {
      window.history.pushState({}, '', '/planning-board');
      setPage('planning-board');
      return;
    }

    setPage(target.page);
  };

  const backToMatches = () => {
    setRequestedMatchId(null);
    setMatchReturnTarget(null);
    openMatchListInTeamEvents();
  };

  const backFromMatchDetail = () => {
    const target = matchReturnTarget;
    setRequestedMatchId(null);
    setRequestedScoreMatchId(null);
    setMatchReturnTarget(null);

    if (target?.page === 'team-events') {
      window.history.pushState({}, '', '/team-events');
      setPage('team-events');
      return;
    }

    if (target?.page === 'command-center') {
      window.history.pushState({}, '', '/');
      setPage('command-center');
      return;
    }

    if (target?.page === 'planning-board') {
      window.history.pushState({}, '', '/planning-board');
      setPage('planning-board');
      return;
    }

    openMatchListInTeamEvents();
  };

  const currentTeamReturnTarget = (): TeamReturnTarget | null => {
    if (page === 'teams') return teamReturnTarget;
    return {
      page,
      matchId: page === 'match-detail' ? requestedMatchId : page === 'lineup' ? requestedLineupMatchId : null,
      teamId: page === 'availability' ? requestedAvailabilityTeamId : null,
      reportType: page === 'reports' ? requestedReportType : null,
      feesView: page === 'fees-payments' ? requestedFeesView : undefined,
    };
  };

  const returnFromTeams = () => {
    const target = teamReturnTarget;
    setTeamReturnTarget(null);
    setRequestedTeamDetailId(null);

    if (!target || target.page === 'teams') {
      window.history.pushState({}, '', '/');
      setPage('command-center');
      return;
    }

    if (target.page === 'matches') {
      openMatchListInTeamEvents(target.teamId);
      return;
    }

    if (target.page === 'match-detail' && target.matchId) {
      setRequestedMatchId(target.matchId);
      window.history.pushState({}, '', `/matches/${target.matchId}`);
      setPage('match-detail');
      return;
    }

    if (target.page === 'lineup' && target.matchId) {
      setRequestedLineupMatchId(target.matchId);
      window.history.pushState({}, '', `/matches/${target.matchId}/lineup`);
      setPage('lineup');
      return;
    }

    if (target.page === 'availability') {
      setRequestedAvailabilityTeamId(target.teamId || null);
      setRequestedAvailabilityMatchId(null);
      window.history.pushState({}, '', '/availability');
      setPage('availability');
      return;
    }

    if (target.page === 'reports') {
      setRequestedReportType(target.reportType || null);
      setPage('reports');
      return;
    }

    if (target.page === 'fees-payments') {
      setRequestedFeesView(target.feesView || 'campaigns');
      setPage('fees-payments');
      return;
    }

    setPage(target.page);
  };

  const openTeams = () => {
    setTeamReturnTarget(currentTeamReturnTarget());
    setRequestedTeamDetailId(null);
    window.history.pushState({}, '', '/teams');
    setPage('teams');
  };

  const viewTeamMatches = (teamId: string) => {
    openMatchListInTeamEvents(teamId);
  };

  const editMatchFromTeamEvents = (matchId: string) => {
    openEditMatchEvent(matchId, 'team-events', { page: 'team-events' });
  };

  const addMatchFromTeamEvents = () => {
    openCreateEvent();
  };

  const openTeamDetail = (teamId: string) => {
    setTeamReturnTarget(currentTeamReturnTarget());
    setRequestedTeamDetailId(teamId);
    window.history.pushState({}, '', '/teams');
    setPage('teams');
  };

  const openParticipationEventsForPlayer = (playerId: string) => {
    setRequestedParticipationPlayerId(playerId);
    setPage('participation-events');
  };

  const openPlayerScheduleReport = (playerId: string) => {
    setRequestedSchedulePlayerId(playerId);
    setRequestedReportType('player_schedule');
    setPage('reports');
  };

  const openParticipationReport = (teamId?: string | null) => {
    setRequestedSchedulePlayerId(null);
    setRequestedParticipationTeamId(teamId || null);
    setRequestedReportType('player_participation');
    setPage('reports');
  };

  const openOutstandingFees = () => {
    setRequestedFeesView('outstanding');
    setPage('fees-payments');
  };

  const openPaymentInbox = () => {
    setRequestedFeesView('payment-inbox');
    setPage('fees-payments');
  };

  const openPlayers = () => {
    setPage('players');
  };

  const openAvailability = (contextOrTeamId?: string | AvailabilityOpenContext | null) => {
    const context = typeof contextOrTeamId === 'object' && contextOrTeamId !== null
      ? contextOrTeamId
      : { teamId: contextOrTeamId || null, matchId: null };
    setRequestedAvailabilityTeamId(context.teamId || null);
    setRequestedAvailabilityMatchId(context.matchId || null);
    setAvailabilityReturnTarget(context.returnTarget ? {
      page: context.returnTarget,
      matchId: requestedMatchId || context.matchId || null,
      matchReturnTarget: context.returnTarget === 'match-detail' ? matchReturnTarget : null,
    } : null);
    const params = new URLSearchParams();
    if (context.returnTarget === 'planning-board') params.set('origin', 'planning-board');
    if (context.matchId) params.set('matchId', context.matchId);
    if (context.teamId) params.set('teamId', context.teamId);
    window.history.pushState({}, '', `/availability${params.toString() ? `?${params.toString()}` : ''}`);
    setPage('availability');
  };

  const backFromAvailability = () => {
    const target = availabilityReturnTarget;
    setAvailabilityReturnTarget(null);
    setRequestedAvailabilityMatchId(null);

    if (target?.page === 'planning-board') {
      window.history.pushState({}, '', '/planning-board');
      setPage('planning-board');
      return;
    }

    if (target?.page === 'lineup' && target.matchId) {
      // Preserve the original Lineup Builder return target across the round trip.
      setRequestedLineupMatchId(target.matchId);
      window.history.pushState({}, '', `/matches/${target.matchId}/lineup`);
      setPage('lineup');
      return;
    }

    if (target?.page === 'match-detail' && target.matchId) {
      openMatch(target.matchId, target.matchReturnTarget || undefined);
      return;
    }

    if (target?.page === 'team-events') {
      window.history.pushState({}, '', '/team-events');
      setPage('team-events');
      return;
    }

    setPage('command-center');
  };

  const navigateFromAvailabilityBreadcrumb = (item: BreadcrumbItem) => {
    if (item.path === '/planning-board') {
      window.history.pushState({}, '', '/planning-board');
      setPage('planning-board');
      return;
    }

    if (item.path === '/team-events') {
      window.history.pushState({}, '', '/team-events');
      setPage('team-events');
      return;
    }

    const match = item.path?.match(/^\/matches\/([^/]+)$/);
    if (match?.[1]) {
      openMatch(match[1], matchReturnTarget || undefined);
    }
  };

  const openTeamRoster = (teamId?: string | null) => {
    setRequestedRosterTeamId(teamId || null);
    setPage('rosters');
  };

  const openTeamEvent = (eventId: string) => {
    setRequestedTeamEventId(eventId);
    window.history.pushState({}, '', `/team-events/${eventId}`);
    setPage('team-events');
  };

  const openPlayerView = () => {
    if (!appUser || !playerViewAvailable) return;
    savePreferredView(appUser.id, 'player');
    window.history.pushState({}, '', '/player');
    setPage('player');
    setMobileNavOpen(false);
  };

  const openCaptainView = () => {
    if (appUser) savePreferredView(appUser.id, 'captain');
    window.history.pushState({}, '', '/');
    setPage('command-center');
    setMobileNavOpen(false);
  };

  const navigateToPage = (key: string, path?: string) => {
    if (!canAccessPage(key)) {
      setPage('command-center');
      setMobileNavOpen(false);
      return;
    }
    if (key === 'player') {
      openPlayerView();
      return;
    }
    if (key === 'workspace-branding' && !playerPortalEnabled) {
      window.history.replaceState({}, '', '/');
      setPage('command-center');
      setMobileNavOpen(false);
      return;
    }
    if (key === 'teams') {
      openTeams();
      setMobileNavOpen(false);
      return;
    }
    if (key === 'participation-events') setRequestedParticipationPlayerId(null);
    if (key === 'availability') {
      setRequestedAvailabilityTeamId(null);
      setRequestedAvailabilityMatchId(null);
      setAvailabilityReturnTarget(null);
    }
    if (key === 'rosters') setRequestedRosterTeamId(null);
    if (key === 'reports') {
      setRequestedSchedulePlayerId(null);
      setRequestedParticipationTeamId(null);
      setRequestedReportType(null);
    }
    if (key === 'fees-payments') setRequestedFeesView('campaigns');
    if (path) window.history.pushState({}, '', path);
    setPage(key);
    setMobileNavOpen(false);
  };

  if (authLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Loading CourtCaptain...</Typography>
            {showAuthReset && (
              <Stack spacing={1} alignItems="center" sx={{ maxWidth: 360 }}>
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  Still loading? Reset the stale staging login session and return to the sign-in screen.
                </Typography>
                <Button size="small" variant="outlined" onClick={() => void signOut()}>
                  Reset Login Session
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>
      </ThemeProvider>
    );
  }

  if (session && passwordSetupRequired) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PasswordSetup />
      </ThemeProvider>
    );
  }

  if (!session || !appUser) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login />
      </ThemeProvider>
    );
  }

  if (appUser.role === 'player' || page === 'player') {
    return (
      <AuthenticatedPlayerView
        timeFormat={appSettings.timeFormat}
        exitLabel="Captain View"
        onExit={appUser.role === 'player' ? undefined : openCaptainView}
      />
    );
  }

  const navContent = (
    <>
      <Toolbar />
      <List sx={{ px: 1 }}>
        {navSections.map((section, sectionIndex) => (
          <Box key={section.label} sx={{ mt: sectionIndex === 0 ? 0 : 1.5 }}>
            {sectionIndex > 0 && <Divider sx={{ mb: 1.5 }} />}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 0.75, fontWeight: 700 }}>
              {section.label}
            </Typography>
            {section.items.filter((item) => (
              (!('adminOnly' in item) || !item.adminOnly || isOwner)
              && (!('playerPortalOnly' in item) || !item.playerPortalOnly || playerPortalEnabled)
              && (!('playerViewOnly' in item) || !item.playerViewOnly || (playerPortalEnabled && Boolean(appUser.linked_player_id)))
              && (!('featureFlag' in item) || item.featureFlag !== 'pairing_compatibility_analytics' || pairingCompatibilityAnalyticsEnabled())
              && (!('feature' in item) || !item.feature || canViewAnyTeamFeature(item.feature as TeamPermissionFeature))
            )).map((item) => (
              <ListItemButton key={item.key} selected={page === item.key} onClick={() => navigateToPage(item.key, 'path' in item ? item.path : undefined)} sx={{ borderRadius: 1, minHeight: 44 }}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={(
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="span">{item.label}</Box>
                      {item.key === 'planning-board' && availabilityBadgeCount > 0 && (
                        <Badge color="error" badgeContent={availabilityBadgeCount} max={99}>
                          <Box component="span" sx={{ width: 8, height: 8 }} />
                        </Badge>
                      )}
                    </Box>
                  )}
                />
              </ListItemButton>
            ))}
          </Box>
        ))}
      </List>
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <AppBar position="fixed" sx={{ zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1 }}>
          <Toolbar sx={{ gap: 2 }}>
            {isMobileNav && (
              <IconButton color="inherit" edge="start" aria-label="Open navigation" onClick={() => setMobileNavOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}
            <SportsTennisIcon sx={{ mr: 1 }} />
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
                CourtCaptain
              </Typography>
              <Typography variant="caption" noWrap component="div" sx={{ color: 'primary.contrastText', opacity: 0.86, lineHeight: 1 }}>
                Manage Your Team. Win More Matches.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
              {playerViewAvailable && (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<SportsTennisIcon />}
                  onClick={openPlayerView}
                  sx={{ borderColor: 'rgba(255,255,255,0.7)' }}
                >
                  Player View
                </Button>
              )}
              {canUseAskCourtCaptain && (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<AutoFixHighIcon />}
                  onClick={() => setAskCourtCaptainOpen(true)}
                  sx={{ borderColor: 'rgba(255,255,255,0.7)' }}
                >
                  Ask CourtCaptain
                </Button>
              )}
              <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                {appUser.display_name}
              </Typography>
              <Button color="inherit" size="small" variant="outlined" onClick={() => void signOut()} sx={{ borderColor: 'rgba(255,255,255,0.7)' }}>
                Logout
              </Button>
            </Stack>
            <Button color="inherit" size="small" onClick={() => void signOut()} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
              Logout
            </Button>
            {playerViewAvailable && (
              <IconButton color="inherit" aria-label="Open Player View" onClick={openPlayerView} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
                <SportsTennisIcon />
              </IconButton>
            )}
            {canUseAskCourtCaptain && (
              <IconButton color="inherit" aria-label="Ask CourtCaptain" onClick={() => setAskCourtCaptainOpen(true)} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
                <AutoFixHighIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
        {isMobileNav ? (
          <Drawer
            variant="temporary"
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' } }}
          >
            {navContent}
          </Drawer>
        ) : !planningWorkflowFullScreenActive ? (
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
            }}
          >
            {navContent}
          </Drawer>
        ) : null}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            minWidth: 0,
            width: { xs: '100%', md: planningWorkflowFullScreenActive ? '100%' : `calc(100% - ${drawerWidth}px)` },
            maxWidth: '100vw',
            boxSizing: 'border-box',
            overflowX: { xs: 'hidden', md: 'visible' },
          }}
        >
          <Toolbar />
          {lookupError && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {lookupError}
            </Typography>
          )}
          {page === 'oauth-consent' ? (
            <OAuthConsent />
          ) : page === 'account' ? (
            <AccountManagement />
          ) : !canAccessPage(page) ? (
            <Box sx={{ maxWidth: 720 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Access Denied</Typography>
              <Typography variant="body2" color="text.secondary">
                Your CourtCaptain permissions do not include this area for your assigned teams.
              </Typography>
            </Box>
          ) : !hasTeamAccess && page !== 'app-settings' ? (
            <Box sx={{ maxWidth: 720 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>No Team Access</Typography>
              <Typography variant="body2" color="text.secondary">
                Your login is active, but no CourtCaptain teams are assigned to your user. Ask an administrator to add you to Team Access.
              </Typography>
            </Box>
          ) : page === 'rosters' ? (
            <RosterManager lookups={visibleLookupsForFeature('roster_management')} refreshLookups={refreshLookups} userPreferences={userPreferences} onUserPreferencesChange={updateUserPreferences} initialTeamId={requestedRosterTeamId} readOnly={!canEditAnyTeamFeature('roster_management')} canViewPartnerPreferences={canViewAnyTeamFeature('partner_preferences')} canEditPartnerPreferences={canEditAnyTeamFeature('partner_preferences')} />
          ) : page === 'teams' ? (
            <TeamManager lookups={visibleLookupsForFeature('roster_management')} refreshLookups={refreshLookups} onViewTeamMatches={viewTeamMatches} userPreferences={userPreferences} onUserPreferencesChange={updateUserPreferences} initialTeamId={requestedTeamDetailId} readOnly={!canEditAnyTeamFeature('roster_management')} onBack={returnFromTeams} canViewPartnerPreferences={canViewAnyTeamFeature('partner_preferences')} canEditPartnerPreferences={canEditAnyTeamFeature('partner_preferences')} canManageTeamAccess={isOwner || canEditAnyTeamFeature('users_permissions')} />
          ) : page === 'matches' ? (
            <TeamEvents
              lookups={visibleLookupsForFeature('team_events')}
              defaultCaptainTeamIds={defaultCaptainTeamIds}
              refreshLookups={refreshLookups}
              timeFormat={appSettings.timeFormat}
              refreshToken={teamEventsRefreshToken}
              readOnly={!canEditAnyTeamFeature('team_events')}
              requestedFilter={deprecatedMatchesFilter}
              onRequestedFilterApplied={() => {
                setRequestedTeamMatchFilterId(null);
                window.history.replaceState({}, '', '/team-events');
                setPage('team-events');
              }}
              onOpenMatchDetail={(matchId) => openMatch(matchId, { page: 'team-events' })}
              onEditMatch={editMatchFromTeamEvents}
              onAddEvent={openCreateEvent}
              onEditEvent={openEditTeamEvent}
              onAddMatch={addMatchFromTeamEvents}
              onOpenLineup={openLineup}
              onOpenAvailability={(teamId) => openAvailability({ teamId, returnTarget: 'team-events' })}
            />
          ) : page === 'event-editor' ? (
            <EventEditor
              userPreferences={userPreferences}
              lookups={visibleLookupsForFeature('team_events')}
              refreshLookups={refreshLookups}
              timeFormat={appSettings.timeFormat}
              mode={requestedEventEditorMode}
              eventId={requestedEventEditorId}
              readOnly={!canEditAnyTeamFeature('team_events')}
              writableTeamIds={isOwner ? lookups.teams.map((team) => team.value) : writableTeamIds}
              backLabel={eventEditorBackLabel}
              onCancel={returnFromEventEditor}
              onSaved={returnFromEventEditor}
              onDeleted={returnFromDeletedEventEditor}
            />
          ) : page === 'match-detail' ? (
            <MatchScheduling
              refreshLookups={refreshLookups}
              lookups={visibleLookupsForFeature('matches_lineups')}
              initialMatchId={requestedMatchId}
              initialScoreEditorOpen={requestedScoreMatchId === requestedMatchId}
              mode="detail"
              onMatchOpened={() => undefined}
              initialEditMatchId={requestedEditMatchId}
              initialCreateMatchOpen={requestedCreateMatch}
              onEditMatchOpened={() => setRequestedEditMatchId(null)}
              onCreateMatchOpened={() => setRequestedCreateMatch(false)}
              onEditMatchRoute={(matchId) => openEditMatchEvent(matchId, 'match-detail', matchReturnTarget || { page: 'team-events' })}
              onOpenLineup={openLineup}
              onBackToMatches={backFromMatchDetail}
              onOpenTeamEvents={() => {
                setRequestedMatchId(null);
                setRequestedScoreMatchId(null);
                setMatchReturnTarget(null);
                window.history.pushState({}, '', '/team-events');
                setPage('team-events');
              }}
              backLabel={matchReturnTarget?.page === 'planning-board' ? 'Planning Board' : matchReturnTarget?.page === 'command-center' ? 'Command Center' : 'Team Events'}
              breadcrumbOrigin={matchReturnTarget?.page === 'planning-board' ? 'planning-board' : 'team-events'}
              isWorkflowFullScreen={planningWorkflowFullScreenActive}
              onExitWorkflowFullScreen={() => setPlanningBoardFullScreen(false)}
              onEditMatchSaved={matchReturnTarget?.page === 'team-events' ? backFromMatchDetail : undefined}
              onCreateMatchSaved={matchReturnTarget?.page === 'team-events' ? backFromMatchDetail : undefined}
              onCreateMatchCancelled={matchReturnTarget?.page === 'team-events' ? backFromMatchDetail : undefined}
              onScoreEditorClosed={() => setRequestedScoreMatchId(null)}
              onOpenAvailability={(teamId, matchId) => openAvailability({ teamId, matchId, returnTarget: 'match-detail' })}
              timeFormat={appSettings.timeFormat}
              userPreferences={userPreferences}
              readOnly={!canEditAnyTeamFeature('matches_lineups')}
              canSendEmail={isOwner}
            />
          ) : page === 'availability' ? (
            <AvailabilityMatrix
              lookups={visibleLookupsForFeature('player_availability')}
              userPreferences={userPreferences}
              onUserPreferencesChange={updateUserPreferences}
              timeFormat={appSettings.timeFormat}
              initialTeamId={requestedAvailabilityTeamId}
              initialMatchId={requestedAvailabilityMatchId}
              readOnly={!canEditAnyTeamFeature('player_availability')}
              onOpenLineup={(matchId, context) => {
                if (availabilityReturnTarget?.page === 'lineup' && availabilityReturnTarget.matchId === matchId) {
                  setRequestedLineupMatchId(matchId);
                  window.history.pushState({}, '', `/matches/${matchId}/lineup`);
                  setPage('lineup');
                } else openLineup(matchId, context);
              }}
              onBack={backFromAvailability}
              backLabel={availabilityReturnTarget?.page === 'planning-board' ? 'Back to Planning Board' : availabilityReturnTarget?.page === 'match-detail' ? 'Back to Match' : availabilityReturnTarget?.page === 'lineup' ? 'Back to Lineup' : 'Back'}
              breadcrumbOrigin={availabilityReturnTarget?.page === 'planning-board' || availabilityReturnTarget?.page === 'match-detail' || availabilityReturnTarget?.page === 'team-events' ? availabilityReturnTarget.page : 'direct'}
              onBreadcrumbNavigate={navigateFromAvailabilityBreadcrumb}
              isWorkflowFullScreen={planningWorkflowFullScreenActive}
              onExitWorkflowFullScreen={() => setPlanningBoardFullScreen(false)}
            />
          ) : page === 'lineup' ? (
            <LineupBuilder
              matchId={requestedLineupMatchId}
              lookups={visibleLookupsForFeature('matches_lineups')}
              onBackToMatch={backFromLineup}
              onOpenAvailability={(matchId, teamId) => openAvailability({ matchId, teamId, returnTarget: 'lineup' })}
              onOpenMatchDetail={(matchId) => openMatch(matchId, matchReturnTarget || undefined)}
              onOpenTeamEvents={() => openMatchListInTeamEvents()}
              breadcrumbOrigin={lineupReturnTarget?.page === 'planning-board' ? 'planning-board' : 'team-events'}
              isWorkflowFullScreen={planningWorkflowFullScreenActive}
              onExitWorkflowFullScreen={() => setPlanningBoardFullScreen(false)}
              userPreferences={userPreferences}
              onUserPreferencesChange={updateUserPreferences}
              timeFormat={appSettings.timeFormat}
              readOnly={!canEditAnyTeamFeature('matches_lineups')}
              canViewPartnerPreferences={canViewAnyTeamFeature('partner_preferences')}
            />
          ) : page === 'participation-events' ? (
            <ParticipationEventsManager lookups={visibleLookupsForFeature('matches_lineups')} timeFormat={appSettings.timeFormat} initialPlayerFilterId={requestedParticipationPlayerId} readOnly={!canEditAnyTeamFeature('matches_lineups')} />
          ) : page === 'planning-board' ? (
            <PlanningBoard
              lookups={visibleLookupsForFeature('planning_board')}
              defaultCaptainTeamIds={defaultCaptainTeamIds}
              userPreferences={userPreferences}
              onUserPreferencesChange={updateUserPreferences}
              timeFormat={appSettings.timeFormat}
              onOpenPlayerSchedule={openPlayerScheduleReport}
              currentUserId={appUserId}
              canRefreshGroupMeEvents={isOwner || canEditAnyTeamFeature('team_events')}
              onOpenAvailability={openAvailability}
              onOpenMatchDetail={(matchId) => openMatch(matchId, { page: 'planning-board' })}
              onOpenLineup={(matchId, context) => openLineup(matchId, { ...context, returnTarget: 'planning-board' })}
              onAvailabilityChangesReviewed={() => void refreshAvailabilityBadge()}
              isFullScreen={planningBoardFullScreen}
              onFullScreenChange={setPlanningBoardFullScreen}
            />
          ) : page === 'season-planning' ? (
            <SeasonPlanning lookups={visibleLookupsForFeature('season_planning')} defaultCaptainTeamIds={defaultCaptainTeamIds} userPreferences={userPreferences} readOnly={!canEditAnyTeamFeature('season_planning')} />
          ) : page === 'reports' ? (
            <Reports
              lookups={visibleLookupsForFeature('reports')}
              defaultCaptainTeamIds={defaultCaptainTeamIds}
              userPreferences={userPreferences}
              onUserPreferencesChange={updateUserPreferences}
              timeFormat={appSettings.timeFormat}
              onOpenParticipationEventsForPlayer={openParticipationEventsForPlayer}
              initialPlayerSchedulePlayerId={requestedSchedulePlayerId}
              initialReportType={requestedReportType}
              initialParticipationTeamId={requestedParticipationTeamId}
              canViewPartnerPreferences={canViewAnyTeamFeature('partner_preferences')}
              readablePartnerPreferenceTeamIds={isOwner ? lookups.teams.map((team) => team.value) : readableTeamIdsForFeature('partner_preferences')}
            />
          ) : page === 'fees-payments' ? (
            <FeesPayments lookups={visibleLookupsForFeature('fees_payments')} defaultCaptainTeamIds={defaultCaptainTeamIds} userPreferences={userPreferences} initialView={requestedFeesView} readOnly={!canEditAnyTeamFeature('fees_payments')} canManageEmailImport={isOwner} />
          ) : page === 'communications' ? (
            <Communications
              initialMatchId={requestedCommunicationsMatchId}
              lookups={visibleLookupsForFeature('communications')}
              defaultCaptainTeamIds={defaultCaptainTeamIds}
              timeFormat={appSettings.timeFormat}
              userPreferences={userPreferences}
              writableTeamIds={isOwner ? lookups.teams.map((team) => team.value) : writableTeamIds}
              onConfigureGroupMe={openTeamDetail}
            />
          ) : page === 'team-events' ? (
            <TeamEvents
              lookups={visibleLookupsForFeature('team_events')}
              defaultCaptainTeamIds={defaultCaptainTeamIds}
              refreshLookups={refreshLookups}
              timeFormat={appSettings.timeFormat}
              refreshToken={teamEventsRefreshToken}
              readOnly={!canEditAnyTeamFeature('team_events')}
              initialEventId={requestedTeamEventId}
              requestedFilter={requestedTeamEventsFilter}
              onEventOpened={() => setRequestedTeamEventId(null)}
              onRequestedFilterApplied={() => setRequestedTeamEventsFilter(null)}
              onOpenMatchDetail={(matchId) => openMatch(matchId, { page: 'team-events' })}
              onEditMatch={editMatchFromTeamEvents}
              onAddEvent={openCreateEvent}
              onEditEvent={openEditTeamEvent}
              onAddMatch={addMatchFromTeamEvents}
              onOpenLineup={openLineup}
              onOpenAvailability={(teamId) => openAvailability({ teamId, returnTarget: 'team-events' })}
            />
          ) : page === 'match-formats' ? (
            <MatchFormats refreshLookups={refreshLookups} />
          ) : page === 'recommendation-strategies' ? (
            <RecommendationStrategies readOnly={viewerOnly} />
          ) : page === 'notification-settings' ? (
            <NotificationSettings lookups={visibleLookups} readOnly={viewerOnly} />
          ) : page === 'workspace-branding' && playerPortalStatusLoaded && playerPortalEnabled && isOwner ? (
            <WorkspaceBranding isAdmin={isOwner} />
          ) : page === 'workspace-branding' ? (
            <Box sx={{ maxWidth: 720 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Workspace Branding unavailable</Typography>
              <Typography variant="body2" color="text.secondary">
                Workspace Branding is available only when the Player Portal is enabled.
              </Typography>
            </Box>
          ) : page === 'groupme-spike' ? (
            <GroupMeSpike isAdmin={isOwner} />
          ) : page === 'tennisrecord-sync' ? (
            <TennisRecordSyncSettings lookups={lookups} isAdmin={isOwner} />
          ) : page === 'users' ? (
            <UserManagement lookups={lookups} refreshLookups={refreshLookups} isAdmin={isOwner} />
          ) : page === 'app-settings' ? (
            <AppSettings
              appSettings={appSettings}
              onSave={updateAppSettings}
              lookups={visibleLookups}
              isAdmin={isOwner}
              exportedBy={appUser.display_name}
              playerPortalEnabled={playerPortalEnabled}
              readOnly={viewerOnly}
            />
          ) : currentEntity ? (
            <CrudPage entity={currentEntity} lookups={visibleLookups} refreshLookups={refreshLookups} userPreferences={userPreferences} onUserPreferencesChange={updateUserPreferences} readOnly={viewerOnly} />
          ) : (
            <CommandCenter
              lookups={visibleLookups}
              onOpenMatch={(matchId) => openMatch(matchId, { page: 'command-center' })}
              onOpenMatchScores={openMatchScores}
              onOpenLineup={openLineup}
              onOpenOutstandingFees={openOutstandingFees}
              onOpenPaymentInbox={openPaymentInbox}
              onOpenParticipationReport={openParticipationReport}
              onOpenPlayers={openPlayers}
              onOpenTeams={openTeams}
              onOpenTeamDetail={openTeamDetail}
              onOpenAvailability={openAvailability}
              onOpenTeamRoster={openTeamRoster}
              onOpenCommunications={(matchId) => { setRequestedCommunicationsMatchId(matchId || null); setPage('communications'); }}
              onOpenMatches={() => openMatchListInTeamEvents()}
              onOpenTeamEvents={() => setPage('team-events')}
              onOpenTeamEvent={openTeamEvent}
              onOpenNotificationSettings={() => setPage('notification-settings')}
              onOpenTennisRecordSync={() => setPage('tennisrecord-sync')}
              captainName={appUser.display_name}
              timeFormat={appSettings.timeFormat}
              userPreferences={userPreferences}
              writableTeamIds={writableTeamIds}
              isOwner={isOwner}
              featureAccess={commandCenterFeatureAccess}
            />
          )}
        </Box>
        {canUseAskCourtCaptain && (
          <AskCourtCaptain
            open={askCourtCaptainOpen}
            onClose={() => setAskCourtCaptainOpen(false)}
            lookups={visibleLookups}
            initialTeamId={askCourtCaptainTeamId}
            initialMatchEventId={askCourtCaptainMatchId}
          />
        )}
      </Box>
    </ThemeProvider>
  );
}

export { entities };
